import { FormEvent, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { createPublicBookingAsync } from "../../api/PublicApi";
import { PublicBookRequest } from "../../models/PublicBookingModels";
import { formatDateTime, toISO } from "../../utils/DateTime";
import PublicCaptcha from "./PublicCaptcha";
import { usePublicSiteData } from "./PublicSiteContext";
import { StageImage, useStage } from "./StagedImageLoading";

const pad = (n: number) => String(n).padStart(2, "0");

const toKey = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const buildTimeSlots = (): string[] => {
  const slots: string[] = [];
  for (let hour = 9; hour <= 21; hour += 1) {
    slots.push(`${pad(hour)}:00`);
    slots.push(`${pad(hour)}:30`);
  }
  return slots;
};

const MIN_BOOKING_HOURS = 4;

const PublicAppointment = () => {
  const { slug } = useParams<{ slug: string }>();
  const website = usePublicSiteData();

  const [form, setForm] = useState({
    name: "",
    email: "",
    service: "",
    phone: "",
    comment: "",
    date: "",
    time: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");
  const [confirmed, setConfirmed] = useState<{
    clientName: string;
    organizationName: string;
    scheduledAt: string;
    phone: string;
    isExistingClient: boolean;
    message: string;
  } | null>(null);
  const [captchaValid, setCaptchaValid] = useState(false);
  const [captchaError, setCaptchaError] = useState("");

  const timeSlots = useMemo(buildTimeSlots, []);

  const appointmentImage =
    website?.settings?.appointmentImageUrl ??
    website?.promoImages[0]?.imageUrl;
  useStage("appointment", appointmentImage ? 1 : 0);

  if (!website) return null;

  const org = website.organization;

  const minBooking = new Date(Date.now() + MIN_BOOKING_HOURS * 60 * 60 * 1000);
  const minDateKey = toKey(minBooking);
  const minTimeKey = `${pad(minBooking.getHours())}:${pad(
    minBooking.getMinutes(),
  )}`;

  const isToday = form.date === minDateKey;
  const isTimeDisabled = (slot: string) => isToday && slot <= minTimeKey;
  const visibleTimeSlots = timeSlots.filter((slot) => !isTimeDisabled(slot));

  const setField = (key: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next: Record<string, string> = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = "Please enter your name.";
    if (!form.phone.trim()) nextErrors.phone = "Please enter your phone number.";
    if (!form.service) nextErrors.service = "Please select a service.";
    if (!form.date) nextErrors.date = "Please select a date.";
    if (!form.time) nextErrors.time = "Please select a time.";
    if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      nextErrors.email = "Please enter a valid email address.";
    }
    if (form.date && form.time) {
      const scheduled = new Date(`${form.date}T${form.time}`);
      if (scheduled.getTime() < minBooking.getTime()) {
        nextErrors.time =
          "Please choose a time at least 4 hours from now.";
      }
    }
    if (Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors);
      return;
    }
    if (!captchaValid) {
      setCaptchaError("Incorrect security answer. Please try again.");
      return;
    }
    if (!slug) return;

    const scheduledAt = toISO(`${form.date}T${form.time}`) ?? "";

    setCaptchaError("");
    setServerError("");
    setSubmitting(true);
    try {
      const request: PublicBookRequest = {
        branchId: null,
        scheduledAt,
        notes: form.comment.trim() || null,
        client: {
          fullName: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || null,
          age: null,
          gender: null,
          notes: null,
        },
        services: form.service
          ? [{ serviceId: form.service, quantity: 1 }]
          : [],
      };
      const res = await createPublicBookingAsync(slug, request);
      if (res.success && res.result) {
        setConfirmed({
          clientName: res.result.clientName,
          organizationName: res.result.organizationName,
          scheduledAt: res.result.scheduledAt,
          phone: res.result.phone,
          isExistingClient: res.result.isExistingClient,
          message: res.message,
        });
      } else {
        setServerError(res.message || "Could not book your appointment.");
      }
    } catch {
      setServerError("Could not book your appointment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="psite-appointment" id="appointment">
      <div className="psite-appointment__card">
        <div className="psite-appointment__form">
          <p className="psite-appointment__kicker">Make An</p>
          <h2>Appointment</h2>
          <p>
            Book a consultation and let our specialists take care of you.
          </p>

          {confirmed ? (
            <div className="psite-appointment__success">
              <p>
                Thank you, <strong>{confirmed.clientName}</strong>! Your
                appointment at{" "}
                <strong>{confirmed.organizationName}</strong> is confirmed for{" "}
                <strong>{formatDateTime(confirmed.scheduledAt)}</strong>.
              </p>
              {confirmed.isExistingClient && (
                <p>{confirmed.message}</p>
              )}
            </div>
          ) : (
            <form className="psite-form" onSubmit={handleSubmit} noValidate>
              <div className="psite-form__row">
                <div className="psite-form__field">
                  <input
                    type="text"
                    placeholder="Name"
                    aria-label="Name"
                    aria-required="true"
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                  />
                  {errors.name && (
                    <p className="psite-feedback__error">{errors.name}</p>
                  )}
                </div>
                <div className="psite-form__field">
                  <input
                    type="email"
                    placeholder="Email"
                    aria-label="Email"
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                  />
                  {errors.email && (
                    <p className="psite-feedback__error">{errors.email}</p>
                  )}
                </div>
              </div>
              <div className="psite-form__row">
                <div className="psite-form__field">
                  <select
                    aria-label="Service"
                    value={form.service}
                    onChange={(e) => setField("service", e.target.value)}
                  >
                    <option value="">Select a service</option>
                    {website.services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                  {errors.service && (
                    <p className="psite-feedback__error">{errors.service}</p>
                  )}
                </div>
                <div className="psite-form__field">
                  <input
                    type="tel"
                    placeholder="Phone"
                    aria-label="Phone"
                    aria-required="true"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                  />
                  {errors.phone && (
                    <p className="psite-feedback__error">{errors.phone}</p>
                  )}
                </div>
              </div>
              <div className="psite-form__row">
                <div className="psite-form__field">
                  <span className="psite-form__label">Preferred date *</span>
                  <input
                    type="date"
                    aria-label="Preferred date"
                    min={minDateKey}
                    value={form.date}
                    onChange={(e) => setField("date", e.target.value)}
                  />
                  {errors.date && (
                    <p className="psite-feedback__error">{errors.date}</p>
                  )}
                </div>
                <div className="psite-form__field">
                  <span className="psite-form__label">Preferred time *</span>
                  <select
                    aria-label="Preferred time"
                    value={form.time}
                    onChange={(e) => setField("time", e.target.value)}
                  >
                    <option value="">Select a time</option>
                    {visibleTimeSlots.map((slot) => (
                      <option key={slot} value={slot}>
                        {slot}
                      </option>
                    ))}
                  </select>
                  {isToday && visibleTimeSlots.length === 0 && (
                    <p className="psite-feedback__error">
                      No times available today. Please choose another date.
                    </p>
                  )}
                  {errors.time && (
                    <p className="psite-feedback__error">{errors.time}</p>
                  )}
                </div>
              </div>
              <textarea
                placeholder="Comment"
                aria-label="Comment"
                value={form.comment}
                onChange={(e) => setField("comment", e.target.value)}
              />
              <PublicCaptcha
                onValidChange={setCaptchaValid}
                onReset={() => setCaptchaError("")}
              />
              {(captchaError || serverError) && (
                <p className="psite-feedback__error">
                  {captchaError || serverError}
                </p>
              )}
              <button
                type="submit"
                className="psite-btn psite-btn--primary"
                disabled={submitting}
              >
                {submitting ? "Booking..." : "Submit"}
              </button>
            </form>
          )}
        </div>

        <div className="psite-appointment__photo">
          {appointmentImage ? (
            <StageImage
              stage="appointment"
              src={appointmentImage}
              alt=""
            />
          ) : (
            <div className="psite-appointment__photo--empty">{org.name}</div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PublicAppointment;
